import React, { useContext } from 'react';
import { FormContext } from '../Form';
import { FastFormField, FormField } from '../FormField';
import { Select, SelectOptions } from './Select';

export const selectFieldTestId = 'select-field';

type FastSelectFieldProps<TValue> = {
  name: string;
  label: string | null;
  options: SelectOptions<TValue>;
  mapValueToKey?: (value: TValue) => string;
  isSmall?: boolean;
  className?: string;
};

type SelectFieldProps<TValue> = {
  warning?: string;
  disabled?: boolean;
  onChange?: (newValue: TValue) => void;
} & FastSelectFieldProps<TValue>;

export const SelectField = <TValue,>({
  name,
  label,
  warning,
  disabled,
  options,
  onChange,
  mapValueToKey,
  className,
}: SelectFieldProps<TValue>) => {
  const formIsDisabled = useContext(FormContext).isDisabled;

  return (
    <FormField name={name} label={label} warning={warning} className={className}>
      {({ form, field, $isValid, $isInvalid }) => {
        const onChangeWrapper = (newValue: TValue) => {
          if (onChange != null) {
            onChange(newValue);
          }
          form.setFieldValue(field.name, newValue);
        };

        return (
          <>
            <Select<TValue>
              name={name}
              value={field.value}
              onChange={onChangeWrapper}
              onBlur={field.onBlur}
              $isValid={$isValid}
              $isInvalid={$isInvalid}
              $hasWarning={!!warning}
              options={options}
              data-testid={selectFieldTestId}
              disabled={form.isSubmitting || formIsDisabled || disabled}
              mapValueToKey={mapValueToKey}
            />
          </>
        );
      }}
    </FormField>
  );
};

export const FastSelectField = <TValue,>({
  name,
  label,
  options,
  isSmall,
  className,
}: FastSelectFieldProps<TValue>) => {
  const formIsDisabled = useContext(FormContext).isDisabled;

  return (
    <FastFormField name={name} label={label} className={className}>
      {({ form, field, $isValid, $isInvalid }) => (
        <Select<TValue>
          name={name}
          value={field.value}
          onChange={(newValue: TValue) => form.setFieldValue(field.name, newValue)}
          onBlur={field.onBlur}
          $isValid={$isValid}
          $isInvalid={$isInvalid}
          isSmall={isSmall}
          options={options}
          data-testid={selectFieldTestId}
          disabled={form.isSubmitting || formIsDisabled}
        />
      )}
    </FastFormField>
  );
};
